import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ApiCall from "../Api/ApiCall";
import { useAuth } from "./AuthContext";

/**
 * Which hat the user is currently wearing.
 *
 * An account holds both capabilities - client and freelancer - from
 * registration onwards, and never loses either. This context tracks the
 * *mode*, which is a preference: it decides what the UI shows, never what the
 * API permits.
 *
 * The mode used to be plain useState inside MainLayout, so switching was lost
 * on every reload and never reached the server. It is now persisted through
 * PUT /user/updateUserRole and mirrored to localStorage so a reload renders
 * the right mode immediately, before the server round-trip lands.
 */
const RoleContext = createContext();

const STORAGE_KEY = "activeRole";
const VALID = ["client", "freelancer"];

const readStoredRole = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return VALID.includes(stored) ? stored : null;
};

export const RoleProvider = ({ children }) => {
  const { isLogin } = useAuth();

  const [role, setRoleState] = useState(() => readStoredRole() || "client");
  // Both are true for any normal account; kept here so the UI can hide the
  // switcher for an account that genuinely only holds one.
  const [capabilities, setCapabilities] = useState({
    client: true,
    freelancer: true,
  });
  const [switching, setSwitching] = useState(false);

  /** Adopts the role the server reports, e.g. from the login response. */
  const hydrateFromUser = useCallback((user) => {
    if (!user) return;

    if (user.roles) {
      setCapabilities({
        client: user.roles.client !== false,
        freelancer: user.roles.freelancer !== false,
      });
    }

    if (VALID.includes(user.activeRole)) {
      setRoleState(user.activeRole);
      localStorage.setItem(STORAGE_KEY, user.activeRole);
    }
  }, []);

  /**
   * Switches mode and persists it.
   *
   * Optimistic: the UI flips immediately and rolls back if the server refuses,
   * so the switch never feels like it hangs on the network.
   */
  const switchRole = useCallback(
    async (nextRole) => {
      if (!VALID.includes(nextRole) || nextRole === role) return role;

      const previous = role;
      setRoleState(nextRole);
      localStorage.setItem(STORAGE_KEY, nextRole);
      setSwitching(true);

      try {
        const res = await ApiCall(
          "PUT",
          "user/updateUserRole",
          { role: nextRole },
          "application/json",
        );

        if (res?.data?.roles) {
          setCapabilities({
            client: res.data.roles.client !== false,
            freelancer: res.data.roles.freelancer !== false,
          });
        }
        return nextRole;
      } catch (err) {
        setRoleState(previous);
        localStorage.setItem(STORAGE_KEY, previous);
        console.error("Could not switch role", err);
        return previous;
      } finally {
        setSwitching(false);
      }
    },
    [role],
  );

  // Forget the mode when the session ends, so the next user on this browser
  // does not inherit it.
  useEffect(() => {
    if (!isLogin) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isLogin]);

  const value = useMemo(
    () => ({
      role,
      /** Kept as `setRole` so existing components need no change. */
      setRole: switchRole,
      switchRole,
      capabilities,
      canSwitch: capabilities.client && capabilities.freelancer,
      switching,
      hydrateFromUser,
    }),
    [role, switchRole, capabilities, switching, hydrateFromUser],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => useContext(RoleContext);
